/**
 * The single SQL-syntax classification entry point the SQL edit modal gates on.
 *
 * Four states rather than two, because "the text is bad" and "no verdict was
 * reached" are different outcomes for the admin: only an actual syntax verdict
 * may be reported as a syntax problem, and anything unclassified must keep the
 * submit control disabled instead of being reported as either.
 *
 * The seam is deliberately the whole contract: callers see one async function
 * and the four-member union, never the parser.
 */

/**
 * Maximum accepted SQL length, matching the maximum the update endpoint
 * enforces.
 *
 * This is necessarily a duplicate of the backend constant: the two validators
 * run in different runtimes (browser bundle vs. Lambda) with no module they can
 * share, so the value exists twice on purpose. The shared syntax corpus carries
 * a case either side of this boundary, so if one copy is changed without the
 * other, the drift surfaces as a test failure rather than as a submission the
 * UI enables and the endpoint then rejects.
 */
export const MAX_SQL_LENGTH = 20_000;

/**
 * - `"valid"` — a parser accepted the text as a single SQL statement.
 * - `"invalid"` — the text was rejected: empty, whitespace-only, or a parse
 *   failure.
 * - `"tooLong"` — the text exceeds `MAX_SQL_LENGTH`; reported separately from
 *   `"invalid"` so the modal's reason text names the length, which is the
 *   problem the admin can act on.
 * - `"unclassified"` — no verdict was reached (for example the parser itself
 *   failed to load). Never treated as valid, never reported as a syntax
 *   problem.
 */
export type SqlClassification = "valid" | "invalid" | "tooLong" | "unclassified";

/**
 * The parser's PostgreSQL-only entry point.
 *
 * Never the package root: the root pulls every dialect the package ships and
 * weighs 2.5 MB, against 301 KB for this build. The deep path is safe to import
 * because the package declares no `exports` map, so no subpath is blocked.
 *
 * The dialect is baked into the build rather than passed per call — this build's
 * default options carry `database: "postgresql"` — which is why `astify` is
 * called with no options below and still applies PostgreSQL grammar.
 *
 * Documented here but deliberately **not** used as the argument to `import()`.
 * See `loadSqlParser` for why the specifier has to appear inline.
 */
export const SQL_PARSER_MODULE = "node-sql-parser/build/postgresql";

/** The only parser method this module uses. */
interface SqlParser {
  astify(sql: string): unknown;
}

/**
 * The parser instance, or `null` when it could not be loaded, memoised across
 * calls.
 *
 * The promise itself is cached rather than its value, so concurrent first calls
 * share one import instead of racing two, and it is cached whether the load
 * succeeded or failed. Caching the failure is deliberate: a verdict that
 * changed between a cold and a warm call — `"unclassified"` for one keystroke
 * and `"valid"` for the next, on the same text — is worse for the admin than a
 * stable fail-closed state they can clear with a reload. The endpoint validates
 * authoritatively regardless, so nothing unsafe gets through either way.
 *
 * A single shared instance is safe because `Parser` holds no per-instance
 * state: `astify` delegates straight to the grammar, and what parser state
 * exists lives at the parser module's own scope, shared by every instance
 * anyway. Constructing one per call would buy no isolation.
 */
let parserLoad: Promise<SqlParser | null> | null = null;

/**
 * Pull the `Parser` constructor out of the loaded namespace and instantiate it.
 *
 * Reads both the named export and the CommonJS default: the package is a
 * CommonJS bundle, and whether its named exports are hoisted onto the namespace
 * or left under `default` depends on the interop the bundler applies. The
 * fallback is not defensive padding — this package's test environment hoists
 * them and a browser build need not, so dropping it would pass every test here
 * and still ship a modal that classifies nothing.
 *
 * Deliberately no "is it really a constructor?" check first. A namespace that
 * does not carry a usable constructor makes this throw — reading a missing
 * export, or `new`-ing something that is not a function — and the caller's catch
 * turns that into the same failed load as an import that never resolved. A
 * separate guard would add a branch with no observable behaviour of its own, so
 * nothing could tell whether it still worked.
 */
function instantiateParser(namespace: unknown): SqlParser {
  const candidate = namespace as {
    Parser?: unknown;
    default?: { Parser?: unknown };
  };
  const ParserCtor = candidate.Parser ?? candidate.default?.Parser;

  return new (ParserCtor as new () => SqlParser)();
}

/**
 * Load the parser on first use, lazily and at most once.
 *
 * Lazy is a requirement rather than an optimisation: 301 KB of grammar does not
 * belong in the initial bundle for a modal most sessions never open, and the
 * gap between "the admin typed something" and "the parser is here" is the
 * pending state the modal already has to render.
 *
 * Resolves `null` instead of rejecting, so no rejected promise is ever stored
 * or awaited twice.
 */
function loadSqlParser(): Promise<SqlParser | null> {
  // The specifier is written inline, as a literal, and that is load-bearing
  // rather than stylistic. Rollup can only follow a dynamic import whose
  // argument it can read statically: given `import(someVariable)` it emits the
  // call unchanged and bundles nothing, so the browser receives a bare
  // specifier it cannot resolve, the import rejects, the `catch` below turns it
  // into `null`, and every classification comes back `"unclassified"` — a
  // permanently disabled submit control with a "checking syntax" reason and no
  // error anywhere. With a literal, Rollup resolves the module and emits it as
  // its own lazily-fetched chunk, which is what keeps 300 KB out of the entry
  // bundle while still actually loading.
  //
  // Node-based tests resolve a bare specifier happily, so this only breaks in a
  // real browser — which is how it reached production once. The guard against a
  // refactor putting the constant back is in `sqlSyntax.load.test.ts`, which
  // asserts this argument is an inline literal.
  parserLoad ??= import("node-sql-parser/build/postgresql")
    .then(instantiateParser)
    .catch(() => null);

  return parserLoad;
}

/**
 * Whether `error` is the parser saying "this is not SQL" rather than the parser
 * breaking.
 *
 * The grammar is generated by PEG.js, which throws an error whose `name` is
 * `"SyntaxError"` but which is **not** an instance of the global `SyntaxError` —
 * it is the generated parser's own class. So the name is the discriminator;
 * `instanceof SyntaxError` is always false here and would misroute every parse
 * failure to `"unclassified"`.
 *
 * Everything else is a fault rather than a verdict: a `RangeError` from
 * exhausting the stack on deeply nested parentheses is the realistic one, and
 * reporting that as `"invalid"` would tell the admin their SQL is broken when
 * what actually happened is that we failed to read it.
 */
function isSyntaxRejection(error: unknown): boolean {
  return error instanceof Error && error.name === "SyntaxError";
}

/**
 * Whether `node` is a parsed statement rather than a placeholder.
 *
 * A bare `;` parses to a one-element array whose element is an empty array, not
 * a statement node, so counting array entries alone would classify `;` as one
 * statement and enable submission on text carrying nothing. The backend
 * validator drops its own equivalent placeholders (a `None`, a bare semicolon
 * node) before counting, and this is that same filter — the two rules have to
 * match for the two validators to agree.
 */
function isStatementNode(node: unknown): boolean {
  if (typeof node !== "object" || node === null || Array.isArray(node)) {
    return false;
  }

  const { type } = node as { type?: unknown };
  return typeof type === "string" && type.length > 0;
}

/**
 * How many statements the parsed result carries.
 *
 * `astify` returns **either** shape, decided by the input rather than by
 * configuration, which is the detail worth stating because assuming one of them
 * silently breaks the other:
 *
 * - a bare AST object for a statement with no trailing semicolon
 *   (`SELECT 1` → `{ type: "select", ... }`),
 * - an array once a semicolon appears (`SELECT 1;` → one entry,
 *   `SELECT 1; SELECT 2` → two), and an **empty** array for text that parses
 *   but carries no statement at all, which is what comment-only text does.
 *
 * So a bare object counts as one and an array is counted by its
 * statement-shaped entries.
 */
function countStatements(ast: unknown): number {
  const nodes = Array.isArray(ast) ? ast : [ast];
  return nodes.filter(isStatementNode).length;
}

/**
 * The syntax verdict for text that has already cleared the empty and length
 * branches.
 *
 * Three outcomes, and which failure maps to which is the whole point:
 * a parser that would not load is `"unclassified"`, a parser that rejected the
 * text is `"invalid"`, and only a single accepted statement is `"valid"`.
 */
async function classifyBySyntax(text: string): Promise<SqlClassification> {
  const parser = await loadSqlParser();

  if (parser === null) {
    // No parser, so no verdict. Never "valid": the whole reason this state
    // exists is that a failed load must not be able to enable a submission.
    return "unclassified";
  }

  let ast: unknown;
  try {
    ast = parser.astify(text);
  } catch (error) {
    return isSyntaxRejection(error) ? "invalid" : "unclassified";
  }

  // Exactly one statement, matching the backend's rule so the two agree: no
  // statement (comment-only text) and more than one (a concatenation) are both
  // rejected. Statement *kind* is not a criterion — an INSERT or a DROP is as
  // valid here as a SELECT — and neither is whether anything it names exists,
  // because nothing is contacted but the grammar.
  return countStatements(ast) === 1 ? "valid" : "invalid";
}

/**
 * Classify `text` as SQL for the purposes of enabling the modal's submit
 * control.
 *
 * Resolves for every input and never rejects: the modal derives the submit
 * control's enabled state from the resolved classification, so a rejection
 * would leave that state underivable.
 *
 * `async` because the parser behind this seam is lazily loaded on first use, so
 * the first call for a session waits on that import while later calls resolve
 * from the memoised one. The empty and length branches still answer without
 * touching the parser at all.
 */
export async function validateSqlSyntax(text: string): Promise<SqlClassification> {
  // Explicit string check, mirroring the backend validator, so a value that is
  // not a string (a JSON null or number reaching this from untyped data) is
  // rejected rather than throwing on `.trim()`.
  if (typeof text !== "string" || text.trim().length === 0) {
    return "invalid";
  }

  // Length is checked BEFORE any parse, and against the raw text — the same
  // length the endpoint measures. Order matters and is part of the contract:
  // an over-long text is very likely also unparseable, and the admin needs to
  // be told about the length, which they can act on, rather than about
  // whatever a parser complains about first.
  if (text.length > MAX_SQL_LENGTH) {
    return "tooLong";
  }

  // The syntax verdict, from the lazily loaded PostgreSQL parser. Everything
  // above this line is decided without it, which is why the length reason text
  // is available even when the parser never arrives.
  return classifyBySyntax(text);
}
